// client/src/components/SavedSessionCard.tsx
import React from 'react';
import {
  SessionData,
  EventGroup,
  ProcessedRaceRow,
  RaceResult,
  EVHistoryEntry,
  LiquidityHistoryEntry,
} from '../types';
import ResultsTable from './ResultsTable';
import MetricsDisplay from './MetricsDisplay';
import {
  getTrueOddsLogic,
  getMidpointOddsLogic,
  getAdjustedBackOddsLogic,
  calculateEVLogic,
} from '../oddsCalculations';
import {
  hasRaceStarted,
  extractLocation,
  getLiquiditySymbol as getLiquiditySymbolUtil,
} from '../utils';
import { processTimeSeriesData } from '../chartUtils';
import { ContentContainer } from './ui/ContentContainer';

export interface SavedSessionCardProps {
  sessionData: SessionData;
  isVisible: boolean;
  activeSessionId: string | null;
  toggleMode: 'static' | 'auto' | 'live';
  timeSinceUpdate?: number;
  refreshCycleSeconds?: number;
  isRefreshing?: boolean;
  onUpdateSession: (
    sessionId: string,
    updateData: Partial<
      Pick<
        SessionData,
        | 'boostedOdds'
        | 'racesToWin'
        | 'applyBackOddsPercentage'
        | 'backOddsPercentage'
        | 'isLive'
      >
    >
  ) => void;
  onRemoveSession: (sessionId: string) => void;
}

const SavedSessionCard: React.FC<SavedSessionCardProps> = ({
  sessionData,
  isVisible,
  activeSessionId,
  toggleMode,
  timeSinceUpdate,
  refreshCycleSeconds,
  isRefreshing,
  onUpdateSession,
  onRemoveSession,
}) => {
  const {
    id: sessionId,
    jockey1,
    jockey2,
    results: currentSessResults,
    racesToWin: currentSessRacesToWin,
    boostedOdds: currentSessBoostedOdds,
    applyBackOddsPercentage: currentSessApplyPerc,
    backOddsPercentage: currentSessBackPerc,
    evHistory: currentSessEVHistory = [],
    liquidityHistory: currentSessLiqHistory = [],
    isLive: sessionIsLive,
  } = sessionData;

  const calculateTotalLiquidityForSession = React.useCallback(
    (rows: RaceResult[]): { symbol: string; value: number; total: number } => {
      if (!rows || rows.length === 0)
        return { symbol: '$', value: 0, total: 0 };
      let totalLiquidityValue = 0;
      let totalSymbolValue = 0;
      const activeRaceRowsInner = rows.filter(
        (row) => !hasRaceStarted(row.marketStartTime)
      );
      activeRaceRowsInner.forEach((row) => {
        totalLiquidityValue += row.liquidity || 0;
        totalSymbolValue += getLiquiditySymbolUtil(row.liquidity).value;
      });
      const avgSymbolValue =
        activeRaceRowsInner.length > 0
          ? Math.floor(totalSymbolValue / activeRaceRowsInner.length)
          : 0;
      const symbol = '$'.repeat(avgSymbolValue || 1);
      return { symbol, value: avgSymbolValue, total: totalLiquidityValue };
    },
    []
  );

  const activeRaces = React.useMemo(
    () => currentSessResults.filter((r) => !hasRaceStarted(r.marketStartTime)),
    [currentSessResults]
  );

  const sessionLayTrueOdds = React.useMemo(
    () => getTrueOddsLogic(activeRaces, currentSessRacesToWin),
    [activeRaces, currentSessRacesToWin]
  );
  const sessionMidpointOdds = React.useMemo(
    () => getMidpointOddsLogic(activeRaces, currentSessRacesToWin),
    [activeRaces, currentSessRacesToWin]
  );
  const sessionParsedOdds = React.useMemo(
    () => parseFloat(currentSessBoostedOdds),
    [currentSessBoostedOdds]
  );

  const sessionLayEV = React.useMemo(
    () =>
      !isNaN(sessionParsedOdds)
        ? calculateEVLogic(sessionLayTrueOdds, sessionParsedOdds)
        : null,
    [sessionLayTrueOdds, sessionParsedOdds]
  );
  const sessionLayEVPercentage = React.useMemo(
    () => (sessionLayEV ? Math.round(sessionLayEV * 100) : null),
    [sessionLayEV]
  );

  const sessionMidpointEV = React.useMemo(
    () =>
      !isNaN(sessionParsedOdds)
        ? calculateEVLogic(sessionMidpointOdds, sessionParsedOdds)
        : null,
    [sessionMidpointOdds, sessionParsedOdds]
  );
  const sessionMidpointEVPercentage = React.useMemo(
    () => (sessionMidpointEV ? Math.round(sessionMidpointEV * 100) : null),
    [sessionMidpointEV]
  );

  const { sessionAdjustedBackOddsNum, sessionAdjustedBackEVPercentage } =
    React.useMemo(() => {
      if (currentSessApplyPerc) {
        const adjBackOdds = getAdjustedBackOddsLogic(
          activeRaces,
          currentSessRacesToWin,
          currentSessBackPerc,
          currentSessApplyPerc
        );
        if (adjBackOdds && !isNaN(sessionParsedOdds)) {
          const adjEV = calculateEVLogic(adjBackOdds, sessionParsedOdds);
          return {
            sessionAdjustedBackOddsNum: adjBackOdds,
            sessionAdjustedBackEVPercentage: adjEV
              ? Math.round(adjEV * 100)
              : null,
          };
        }
        return {
          sessionAdjustedBackOddsNum: adjBackOdds,
          sessionAdjustedBackEVPercentage: null,
        };
      }
      return {
        sessionAdjustedBackOddsNum: null,
        sessionAdjustedBackEVPercentage: null,
      };
    }, [
      activeRaces,
      currentSessRacesToWin,
      currentSessBackPerc,
      currentSessApplyPerc,
      sessionParsedOdds,
    ]);

  const { symbol: sessionLiquiditySymbol, total: sessionTotalLiquidity } =
    React.useMemo(
      () => calculateTotalLiquidityForSession(activeRaces),
      [activeRaces, calculateTotalLiquidityForSession]
    );

  const { allEvents: sessionTableEvents } = React.useMemo(() => {
    if (!currentSessResults || currentSessResults.length === 0)
      return { allEvents: [] };
    const eventGroups: Record<string, ProcessedRaceRow[]> = {};
    currentSessResults.forEach((tableRowSource: RaceResult) => {
      if (hasRaceStarted(tableRowSource.marketStartTime)) return;
      const location = extractLocation(tableRowSource.event);
      if (!eventGroups[location]) eventGroups[location] = [];
      eventGroups[location].push({
        ...tableRowSource,
        raceNumber: tableRowSource.raceNumber || 'N/A',
        liquidityValue: tableRowSource.liquidity,
      });
    });
    const allEventsArray: EventGroup[] = Object.entries(eventGroups)
      .map(([location, rowsForEventGroup]) => ({
        location,
        rows: rowsForEventGroup.sort(
          (a, b) =>
            new Date(a.marketStartTime).getTime() -
            new Date(b.marketStartTime).getTime()
        ),
      }))
      .sort((groupA, groupB) => {
        const earliestA = Math.min(
          ...groupA.rows.map((r) => new Date(r.marketStartTime).getTime())
        );
        const earliestB = Math.min(
          ...groupB.rows.map((r) => new Date(r.marketStartTime).getTime())
        );
        return earliestA - earliestB;
      });
    return { allEvents: allEventsArray };
  }, [currentSessResults]);

  const processedSessEVHistData = React.useMemo(
    () => processTimeSeriesData(currentSessEVHistory as EVHistoryEntry[]),
    [currentSessEVHistory]
  );
  const processedSessLiqHistData = React.useMemo(
    () =>
      processTimeSeriesData(currentSessLiqHistory as LiquidityHistoryEntry[]),
    [currentSessLiqHistory]
  );

  const getEVClass = (evValue: number | null | undefined) => {
    if (evValue === null || evValue === undefined)
      return 'text-text-secondary-light dark:text-text-secondary-dark';
    return evValue > 0 ? 'text-emerald-500' : 'text-red-500';
  };

  if (!isVisible) {
    return null;
  }

  return (
    <ContentContainer
      isLive={sessionIsLive}
      toggleMode={toggleMode}
      timeSinceUpdate={timeSinceUpdate}
      refreshCycleSeconds={refreshCycleSeconds}
      isRefreshing={isRefreshing}
    >
      <div className='flex justify-between items-center'>
        <div className='flex items-center space-x-4'>
          <h3 className='text-lg font-semibold text-text-primary-light dark:text-text-primary-dark'>
            {jockey1}
            {jockey2 ? ` / ${jockey2}` : ''}
          </h3>
        </div>
      </div>

      <div className='flex gap-4 items-end'>
        <div className='flex-1'>
          <label className='block text-sm font-medium text-text-primary-light dark:text-text-primary-dark mb-1'>
            Boosted Odds
          </label>
          <input
            type='number'
            value={currentSessBoostedOdds}
            onChange={(e) =>
              onUpdateSession(sessionId, {
                boostedOdds: e.target.value,
              })
            }
            placeholder='e.g., 2.80'
            step='0.01'
            min='1'
            inputMode='decimal'
            className='block w-full rounded-md border-0 py-2.5 px-3.5 text-sm leading-6 shadow-sm ring-1 ring-inset ring-border-light focus:ring-1 focus:ring-inset focus:ring-border-light bg-surface-light text-text-primary-light dark:bg-surface-darker dark:text-text-primary-dark dark:ring-input-border-dark dark:focus:ring-input-border-dark'
          />
        </div>
        <div className='flex-1'>
          <label className='block text-sm font-medium text-text-primary-light dark:text-text-primary-dark mb-1'>
            Races to Win: {currentSessRacesToWin}
          </label>
          <input
            type='range'
            min='1'
            max='5'
            value={currentSessRacesToWin}
            onChange={(e) =>
              onUpdateSession(sessionId, {
                racesToWin: parseInt(e.target.value, 10),
              })
            }
            className='w-full h-2 bg-surface-light dark:bg-surface-dark rounded-lg appearance-none cursor-pointer accent-white dark:accent-black'
          />
        </div>
      </div>

      <ResultsTable
        allEvents={sessionTableEvents}
        isLive={sessionIsLive && toggleMode !== 'static'}
        toggleMode={toggleMode}
      />

      <MetricsDisplay
        layOddsValue={
          sessionLayTrueOdds ? sessionLayTrueOdds.toFixed(2) : 'N/A'
        }
        layEVPercentage={sessionLayEVPercentage}
        layEVClass={getEVClass(sessionLayEV)}
        midpointOddsValue={
          sessionMidpointOdds ? sessionMidpointOdds.toFixed(2) : 'N/A'
        }
        midpointEVPercentage={sessionMidpointEVPercentage}
        midpointEVClass={getEVClass(sessionMidpointEV)}
        totalLiquidityValue={`$${Math.floor(sessionTotalLiquidity).toLocaleString()}`}
        liquiditySymbol={sessionLiquiditySymbol}
        adjustedBackOddsValue={
          currentSessApplyPerc && sessionAdjustedBackOddsNum
            ? sessionAdjustedBackOddsNum.toFixed(2)
            : undefined
        }
        adjustedBackEVPercentage={
          currentSessApplyPerc ? sessionAdjustedBackEVPercentage : undefined
        }
        adjustedBackEVClass={
          currentSessApplyPerc
            ? getEVClass(sessionAdjustedBackEVPercentage)
            : undefined
        }
        applyBackOddsPercentage={currentSessApplyPerc}
        backOddsPercentageValue={currentSessBackPerc}
        evHistory={processedSessEVHistData.data}
        liquidityHistory={processedSessLiqHistData.data}
        evTimeScale={processedSessEVHistData.timeScale}
        liquidityTimeScale={processedSessLiqHistData.timeScale}
        isLive={sessionIsLive && toggleMode !== 'static'}
        areChartsVisible={true}
        baseChartId={`session-${sessionId}`}
        isLoading={false}
      />

      <div className='flex justify-between items-center'>
        <button
          onClick={() => onRemoveSession(sessionId)}
          className='w-full px-6 py-3 text-base font-semibold text-red-400 bg-transparent hover:bg-red-500/10 rounded-lg border border-red-400/50 backdrop-blur-sm transition-all duration-200 hover:shadow-lg hover:border-red-400'
        >
          Remove from Watchlist
        </button>
      </div>
    </ContentContainer>
  );
};

export default SavedSessionCard;
